import React, { Suspense, useState, useCallback } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { SplashScreen } from "@/components/SplashScreen";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";

// Lazy load non-homepage routes
const ServicesPage = React.lazy(() => import("./pages/ServicesPage"));
const ClientArea = React.lazy(() => import("./pages/ClientArea"));
const Auth = React.lazy(() => import("./pages/Auth"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const UsersManagement = React.lazy(() => import("./pages/admin/UsersManagement"));
const CreateUser = React.lazy(() => import("./pages/admin/CreateUser"));
const ProductsManagement = React.lazy(() => import("./pages/admin/ProductsManagement"));
const SellersManagement = React.lazy(() => import("./pages/admin/SellersManagement"));
const SellerLeads = React.lazy(() => import("./pages/admin/SellerLeads"));
const AllLeads = React.lazy(() => import("./pages/admin/AllLeads"));
const ClientsManagement = React.lazy(() => import("./pages/admin/ClientsManagement"));
const FinancialDashboard = React.lazy(() => import("./pages/admin/FinancialDashboard"));
const AccountsManagement = React.lazy(() => import("./pages/admin/AccountsManagement"));
const Settings = React.lazy(() => import("./pages/admin/Settings"));
const SellerDashboard = React.lazy(() => import("./pages/seller/SellerDashboard"));
const NewLead = React.lazy(() => import("./pages/seller/NewLead"));
const LeadHistory = React.lazy(() => import("./pages/seller/LeadHistory"));
const ProductsCatalog = React.lazy(() => import("./pages/seller/ProductsCatalog"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

import { AdminRoute } from "./components/AdminRoute";
import { AdminLayout } from "./components/admin/AdminLayout";
import { SellerRoute } from "./components/SellerRoute";
import { SellerLayout } from "./components/seller/SellerLayout";

const queryClient = new QueryClient();

const isPWA = window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;

const App = () => {
  const [showSplash, setShowSplash] = useState(isPWA);
  const hideSplash = useCallback(() => setShowSplash(false), []);

  return (
    <>
      {showSplash && <SplashScreen onFinish={hideSplash} />}
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Carregando...</div>}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/servicos" element={<ServicesPage />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/area-cliente" element={<ClientArea />} />
                  {/* Admin routes */}
                  <Route path="/admin" element={<AdminRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/usuarios" element={<AdminRoute><AdminLayout><UsersManagement /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/novo-usuario" element={<AdminRoute><AdminLayout><CreateUser /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/produtos" element={<AdminRoute><AdminLayout><ProductsManagement /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/vendedores" element={<AdminRoute><AdminLayout><SellersManagement /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/vendedores/:sellerId/leads" element={<AdminRoute><AdminLayout><SellerLeads /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/leads" element={<AdminRoute><AdminLayout><AllLeads /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/clientes" element={<AdminRoute><AdminLayout><ClientsManagement /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/financeiro" element={<AdminRoute><AdminLayout><FinancialDashboard /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/contas" element={<AdminRoute><AdminLayout><AccountsManagement /></AdminLayout></AdminRoute>} />
                  <Route path="/admin/configuracoes" element={<AdminRoute><AdminLayout><Settings /></AdminLayout></AdminRoute>} />
                  {/* Seller routes */}
                  <Route path="/vendedor" element={<SellerRoute><SellerLayout><SellerDashboard /></SellerLayout></SellerRoute>} />
                  <Route path="/vendedor/novo-lead" element={<SellerRoute><SellerLayout><NewLead /></SellerLayout></SellerRoute>} />
                  <Route path="/vendedor/historico" element={<SellerRoute><SellerLayout><LeadHistory /></SellerLayout></SellerRoute>} />
                  <Route path="/vendedor/sistemas" element={<SellerRoute><SellerLayout><ProductsCatalog /></SellerLayout></SellerRoute>} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </>
  );
};

export default App;
